import { Component, Inject, Input, OnInit, inject, ElementRef } from '@angular/core';
import { AbstractControl, ControlContainer, NgForm, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Classifier, CountryService } from '@nano/common/classifier';
import { IdentificationCodeType, LEI_PATTERN, PartyIdentificationGlossaryService, PartyIdentificationType } from '@nano/common/party-identification';
import { CONFIG } from '@nano/core/config';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { differenceInCalendarDays } from 'date-fns';
import { Observable } from 'rxjs';
import { PaymentIdentificationType } from './payment-identification';
import { FormService } from '@nano/common/form';
import { PaymentType } from '@nano/payment/payment';
import { BeneficiaryNameSyncService } from '../beneficiary-name-sync.service';

@UntilDestroy()
@Component({
  selector: 'app-payment-identification',
  templateUrl: './payment-identification.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  providers: [BeneficiaryNameSyncService],
  standalone: false
})
export class PaymentIdentificationComponent implements OnInit {
  @Input() identificationClassControl: UntypedFormControl;
  @Input() identificationTypeControl: UntypedFormControl;
  @Input() identificationCodeTypeControl: UntypedFormControl;
  @Input() idControl: UntypedFormControl;
  @Input() birthDateControl: UntypedFormControl;
  @Input() countryOfBirthControl: UntypedFormControl;
  @Input() provinceOfBirthControl: UntypedFormControl;
  @Input() cityOfBirthControl: UntypedFormControl;
  @Input() nameControl: UntypedFormControl;
  @Input() surnameControl: UntypedFormControl;
  @Input() beneficiaryNameControl: AbstractControl;
  @Input() nameControlAvailable = true;
  @Input() surnameControlAvailable = true;
  @Input() displayIdentificationAsSelect: boolean;
  @Input() placeholder: string;
  @Input() label: string;
  @Input() paymentType?: PaymentType;
  @Input() allowedClasses: string[] | null = null;
  @Input() identificationTypeDefault: string | null = null;
  @Input() prefilledControls: UntypedFormControl[] = [];
  @Input() hideIdentificationClass = false;
  @Input() hideOtherTypeIdControl = false;

  readonly countries$: Observable<Classifier[]>;
  readonly personIdentificationCodeTypes$: Observable<Classifier[]>;
  readonly organizationIdentificationCodeTypes$: Observable<Classifier[]>;
  readonly beneficiaryIdentificationDocuments$: Observable<Classifier[]>;

  readonly paymentIdentificationTypeCodes$ = (): Observable<Classifier[]> => {
    const identificationCodeTypes$ = this.isIdentificationPrivate ? this.personIdentificationCodeTypes$ : this.organizationIdentificationCodeTypes$;

    return this.isCurrencyCloudPaymentsControls ? this.beneficiaryIdentificationDocuments$ : identificationCodeTypes$;
  };

  readonly PaymentIdentificationClasses = Object.values(PartyIdentificationType);
  readonly PaymentIdentificationType = PaymentIdentificationType;
  readonly PaymentIdentificationTypes = (): string[] =>
    this.isIdentificationPrivate
      ? [PaymentIdentificationType.DATE_PLACE_OF_BIRTH, PaymentIdentificationType.OTHER]
      : [PaymentIdentificationType.BIC_BEI, PaymentIdentificationType.OTHER];

  private readonly todayDate = new Date();
  private readonly dataCyNameCache = new Map<AbstractControl, string | null>();

  private config = inject(CONFIG);

  private readonly leiValidator: ValidatorFn = Validators.pattern(LEI_PATTERN);
  private readonly bicPattern = this.config.paymentIdentificationBicPattern;
  private readonly identificationMaxLength = this.config.paymentIdentificationMaxLength;
  readonly nameMaxLength = this.config.paymentBeneficiaryNameMaxLength;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private countryService: CountryService,
    private partyIdentificationGlossaryService: PartyIdentificationGlossaryService,
    private elementRef: ElementRef,
    private formService: FormService,
    private beneficiaryNameSync: BeneficiaryNameSyncService
  ) {
    this.displayIdentificationAsSelect = this.config.displayIdentificationAsSelect;

    this.countries$ = this.countryService.worldwideCountries$;
    this.personIdentificationCodeTypes$ = this.partyIdentificationGlossaryService.personIdentificationCodeTypes$;
    this.organizationIdentificationCodeTypes$ = this.partyIdentificationGlossaryService.organizationIdentificationCodeTypes$;
    this.beneficiaryIdentificationDocuments$ = this.partyIdentificationGlossaryService.beneficiaryIdentificationDocuments$;
  }

  get identificationClass(): PartyIdentificationType {
    return this.identificationClassControl.value as PartyIdentificationType;
  }

  get identificationType(): PaymentIdentificationType {
    return this.identificationTypeControl?.value as PaymentIdentificationType;
  }

  get isIdentificationPrivate(): boolean {
    return PartyIdentificationType.PRIVATE === this.identificationClass;
  }

  get isCurrencyCloudPaymentsControls(): boolean {
    return !!this.nameControl && !!this.surnameControl;
  }

  get showNameControl(): boolean {
    return !!this.surnameControl?.enabled;
  }

  get nameControlLabelKey(): string {
    return this.showNameControl ? 'reg.details.personGivenName.label' : 'settings.profileSettings.profile-information.companyName';
  }

  birthDateDisabled = (current: Date): boolean => differenceInCalendarDays(current, this.todayDate) > 0;

  controlDataCy(control?: AbstractControl | null): string | null {
    if (!control) {
      return null;
    }

    const cached = this.dataCyNameCache.get(control);
    if (cached !== undefined) {
      return cached;
    }

    const parent = control.parent as UntypedFormGroup | null;
    if (!parent) {
      return null;
    }

    const name = Object.keys(parent.controls).find((key) => parent.controls[key] === control) ?? null;
    this.dataCyNameCache.set(control, name);
    return name;
  }

  ngOnInit(): void {
    if (this.hideOtherTypeIdControl && this.identificationType !== PaymentIdentificationType.BIC_BEI) {
      this.idControl?.clearValidators();
      this.idControl?.reset();
      this.idControl?.disable();
      this.idControl?.updateValueAndValidity();
    }

    if (this.isCurrencyCloudPaymentsControls) {
      this.beneficiaryNameSync.connect({
        beneficiaryName: this.beneficiaryNameControl,
        name: this.nameControl,
        surname: this.surnameControl,
        identification: this.identificationClassControl
      });
    }

    if (this.identificationClassControl.value) {
      this.setNameSurname(this.identificationClassControl.value as PartyIdentificationType);
    }
    this.subscribeIdentificationClassControlValueChanges();

    if (this.identificationTypeControl) {
      this.subscribeIdentificationTypeControlValueChanges();
    }

    if (this.identificationCodeTypeControl) {
      this.subscribeIdentificationCodeTypeControlValueChanges();
    }

    if (this.identificationClass) {
      this.identificationTypeControl?.enable();
    }

    if (this.identificationType) {
      this.updateIdentificationTypeDependentControls(this.identificationType);
    }
  }

  private subscribeIdentificationClassControlValueChanges(): void {
    let previousValue: PartyIdentificationType | undefined;

    this.identificationClassControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value: PartyIdentificationType) => {
      if (
        (previousValue !== undefined && value === null) ||
        (previousValue !== undefined && value !== previousValue) ||
        (previousValue === undefined && value === null) ||
        (previousValue === undefined && value !== null && this.identificationTypeControl.value == null)
      ) {
        this.resetControls();
        if (value) {
          this.enableControls(value);
        } else {
          this.disableControls();
        }
      }

      previousValue = value;
    });
  }

  private resetControls(): void {
    if (!this.keepsIdentificationCodeControlsOnReset()) {
      this.identificationCodeTypeControl?.disable();
      this.idControl?.disable();
    }
    this.identificationTypeControl?.reset();
    this.nameControl?.disable();
    this.surnameControl?.disable();
  }

  private enableControls(value: PartyIdentificationType): void {
    this.setNameSurname(value);
    this.identificationTypeControl?.enable();
    if (!this.identificationClassControl.errors) {
      this.formService.cleanFormItemAndExplain(this.elementRef);
    }
    if (this.identificationCodeTypeControl.value) {
      this.identificationCodeTypeControl?.enable();
    }
    if (this.idControl.value) {
      this.idControl?.enable();
    }
    this.applyIdentificationTypeDefault();
  }

  private applyIdentificationTypeDefault(): void {
    if (!this.identificationTypeDefault || !this.identificationTypeControl || this.identificationTypeControl.value) {
      return;
    }

    this.identificationTypeControl.setValue(this.identificationTypeDefault);
  }

  private disableControls(): void {
    this.identificationTypeControl?.disable();
    if (this.identificationCodeTypeControl.value) {
      this.identificationCodeTypeControl?.disable();
    }
    if (this.idControl.value) {
      this.idControl?.disable();
    }
    this.birthDateControl?.disable();
    this.countryOfBirthControl?.disable();
    this.provinceOfBirthControl?.disable();
    this.cityOfBirthControl?.disable();
    this.nameControl?.disable();
  }

  private keepsIdentificationCodeControlsOnReset(): boolean {
    return [PaymentType.TARGET2, PaymentType.CURRENCY_CLOUD, PaymentType.PAYMENT_GATEWAY].includes(this.paymentType);
  }

  private setNameSurname(value: PartyIdentificationType): void {
    if (!this.isCurrencyCloudPaymentsControls) {
      return;
    }

    this.identificationTypeControl.setValue(PaymentIdentificationType.OTHER);
    this.beneficiaryNameSync.apply(value, { name: this.nameControlAvailable, surname: this.surnameControlAvailable });
  }

  private otherTypeControls = () => (this.hideOtherTypeIdControl ? [this.identificationCodeTypeControl] : [this.identificationCodeTypeControl, this.idControl]);
  private bicBeiTypeControls = () => [this.idControl];
  private datePlaceOfBirthTypeControls = () =>
    [this.birthDateControl, this.countryOfBirthControl, this.provinceOfBirthControl, this.cityOfBirthControl].filter(Boolean);

  private subscribeIdentificationTypeControlValueChanges(): void {
    this.identificationTypeControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: PaymentIdentificationType) => this.updateIdentificationTypeDependentControls(value));
  }

  private subscribeIdentificationCodeTypeControlValueChanges(): void {
    this.identificationCodeTypeControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.applyIdentificationCodeTypeValidators());
  }

  private applyIdentificationCodeTypeValidators(): void {
    if (this.hideOtherTypeIdControl || !this.idControl) {
      return;
    }

    this.idControl.removeValidators(this.leiValidator);

    if (this.identificationCodeTypeControl?.value === IdentificationCodeType.LEI) {
      this.idControl.addValidators(this.leiValidator);
    }

    this.idControl.updateValueAndValidity();
  }

  private isPrefilledControl(control: UntypedFormControl): boolean {
    return this.prefilledControls.includes(control);
  }

  isControlVisible(control?: UntypedFormControl | null): boolean {
    return !!control && (control.enabled || this.isPrefilledControl(control));
  }

  private clearUnusedControls(controls: UntypedFormControl[]): void {
    controls
      .filter((control) => !this.isPrefilledControl(control))
      .forEach((control) => {
        control.reset();
        control.disable();
      });
  }

  private enableUsedControls(controls: UntypedFormControl[]): void {
    controls.filter((control) => !this.isPrefilledControl(control)).forEach((control) => control.enable());
  }

  private updateIdentificationTypeDependentControls(identificationType: PaymentIdentificationType): void {
    switch (identificationType) {
      case PaymentIdentificationType.OTHER:
        if (!this.isCurrencyCloudPaymentsControls) {
          this.otherTypeControls()
            .filter((control) => control === this.idControl)
            .forEach((control) => {
              control.setValidators([Validators.required, Validators.maxLength(this.identificationMaxLength)]);
            });
          this.applyIdentificationCodeTypeValidators();
          this.clearUnusedControls(
            this.bicBeiTypeControls()
              .concat(this.datePlaceOfBirthTypeControls())
              .filter((control) => !this.otherTypeControls().includes(control))
          );
        } else {
          if (this.idControl.enabled) {
            this.enableUsedControls(this.otherTypeControls());
          }
          return;
        }
        this.enableUsedControls(this.otherTypeControls());
        break;
      case PaymentIdentificationType.BIC_BEI:
        this.clearUnusedControls(
          this.otherTypeControls()
            .concat(this.datePlaceOfBirthTypeControls())
            .filter((control) => !this.bicBeiTypeControls().includes(control))
        );
        this.bicBeiTypeControls().forEach((control) => control.addValidators([Validators.pattern(this.bicPattern)]));
        this.enableUsedControls(this.bicBeiTypeControls());
        break;
      case PaymentIdentificationType.DATE_PLACE_OF_BIRTH:
        this.clearUnusedControls(
          this.otherTypeControls()
            .concat(this.bicBeiTypeControls())
            .filter((control) => !this.datePlaceOfBirthTypeControls().includes(control))
        );
        this.enableUsedControls(this.datePlaceOfBirthTypeControls());
        break;
    }
  }
}
